import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"
import { toast } from "./Toast"
import { AddressFields } from "./AddressFields"

export type ObjektEditData = {
  id: number
  number: number | null
  name: string
  street: string | null
  zip: string | null
  city: string | null
  customer_id: number
  kunde_name: string | null
  bunchofkeys: string | null
  description: string | null
}

/**
 * Objekt (fortytools-Facility) bearbeiten. Speichert via
 * PATCH /api/v1/projects/objekte/{id} (provider-aware, fortytools).
 * Name ist Pflicht (fortytools verlangt >=3 Zeichen).
 */
export function ObjektEditModal({
  open, onClose, tenantSlug, objekt, onSaved,
}: {
  open: boolean
  onClose: () => void
  tenantSlug: string
  objekt: ObjektEditData | null
  onSaved?: () => void
}) {
  const qc = useQueryClient()
  const [name, setName] = useState("")
  const [street, setStreet] = useState("")
  const [zip, setZip] = useState("")
  const [city, setCity] = useState("")
  const [keys, setKeys] = useState("")
  const [notes, setNotes] = useState("")
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (open && objekt) {
      setName(objekt.name || "")
      setStreet(objekt.street || "")
      setZip(objekt.zip || "")
      setCity(objekt.city || "")
      setKeys(objekt.bunchofkeys || "")
      setNotes(objekt.description || "")
      setErr(null)
    }
  }, [open, objekt])

  const save = useMutation({
    mutationFn: async () => {
      if (!objekt) throw new Error("Kein Objekt")
      const res = await fetch(`/api/v1/projects/objekte/${objekt.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          tenant: tenantSlug,
          customer_id: objekt.customer_id,
          name: name.trim(),
          street: street.trim(),
          zipcode: zip.trim(),
          city: city.trim(),
          bunchofkeys: keys.trim(),
          description: notes.trim(),
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || json.error) throw new Error(json.error || `HTTP ${res.status}`)
      return json
    },
    onSuccess: () => {
      toast("✓ Objekt aktualisiert")
      qc.invalidateQueries({ queryKey: ["objekte", tenantSlug] })
      onSaved?.()
      onClose()
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : String(e)
      setErr(msg)
      toast("Speichern fehlgeschlagen: " + msg, "error")
    },
  })

  if (!open || !objekt) return null

  const submit = (ev: React.FormEvent) => {
    ev.preventDefault()
    setErr(null)
    if (name.trim().length < 3) { setErr("Objektname muss mindestens 3 Zeichen haben"); return }
    save.mutate()
  }

  return createPortal(
    <div
      className="pp-overlay oben"
      data-portal-modal
      style={{
        position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "24px 16px", overflowY: "auto", backdropFilter: "blur(4px)",
      }}
      onMouseDown={(ev) => { if (ev.target === ev.currentTarget) onClose() }}
    >
      <form
        onSubmit={submit}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-card)", borderRadius: 12, padding: 28,
          maxWidth: 520, width: "100%",
          boxShadow: "0 20px 60px rgba(0,0,0,.2), 0 0 0 1px rgba(0,0,0,.05)",
          margin: "auto",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            Objekt bearbeiten
            {objekt.number != null && <span style={{ color: "var(--text-muted)", fontWeight: 400 }}> · Nr. {objekt.number}</span>}
          </h3>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: 4 }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {objekt.kunde_name && (
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
            Kunde: {objekt.kunde_name}
          </div>
        )}

        {err && <div className="pp-alert pp-alert-err" style={{ marginBottom: 12 }}>{err}</div>}

        <F label="Objekt-Name *" style={{ marginBottom: 12 }}>
          <input value={name} onChange={(e) => setName(e.target.value)} required />
        </F>
        <AddressFields
          labelStrasse="Straße"
          wert={{ street, zip, city }}
          onChange={(w) => { setStreet(w.street); setZip(w.zip); setCity(w.city) }}
        />
        <F label="Schlüsselbund" style={{ marginBottom: 12 }}>
          <input value={keys} onChange={(e) => setKeys(e.target.value)} placeholder="z.B. Schlüssel-Nr." />
        </F>
        <F label="Notizen" style={{ marginBottom: 16 }}>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
        </F>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 13 }}>
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={save.isPending}
            className={cn("pp-btn pp-btn-primary", save.isPending && "opacity-50 cursor-not-allowed")}
            style={{ fontSize: 13 }}
          >
            {save.isPending ? "Speichert..." : "Speichern"}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  )
}

function F({ label, children, style }: { label: React.ReactNode; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={style}>
      <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4, color: "var(--text-secondary)" }}>
        {label}
      </label>
      {children}
    </div>
  )
}
